import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import createCroppedImage from '../../helpers/createCroppedImage/createCroppedImage'

import {
  clearCroppedImageCache,
  getCroppedImageCache,
  setCroppedImageCache,
} from '../../services/CroppedImageCache/croppedImageCache'

const DEFAULT_CONTAINER_DIMENSIONS = { width: 400, height: 300 }
const MIN_DIMENSION_CHANGE = 5
const DIMENSION_SETTLE_DELAY_MS = 500
const MAX_DIMENSION_MEASURE_ATTEMPTS = 5

const parseCropData = (value) => {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (error) {
      console.error('[useImageCropping] Failed to parse crop data', error)
      return null
    }
  }

  return value
}

const getInitialCropData = (initialCropArea) => {
  if (!initialCropArea) {
    return null
  }

  return parseCropData(initialCropArea)
}

export default function useImageCropping({
  cropImagesFlag,
  initialCropArea,
  savedImageUrl,
  memoryId,
  pageNumber,
  positionIndex,
}) {
  const cropRegionRef = useRef(null)
  const [savedCropData, setSavedCropData] = useState(() => getInitialCropData(initialCropArea))
  const [croppedImageUrl, setCroppedImageUrl] = useState('')
  const [processingCrop, setProcessingCrop] = useState(false)
  const [containerDimensions, setContainerDimensions] = useState(() => ({ ...DEFAULT_CONTAINER_DIMENSIONS }))
  const measurementTimeoutRef = useRef(null)
  const measureAttemptsRef = useRef(0)

  const calculateTargetDimensions = useCallback(() => {
    const ref = cropRegionRef.current
    if (!ref) {
      return null
    }

    const { width, height } = ref.getBoundingClientRect()
    if (width > 0 && height > 0) {
      return { width, height }
    }

    return null
  }, [])

  function getUpdatedContainerDimensions(prev, width, height) {
    const widthDiff = Math.abs(prev.width - width)
    const heightDiff = Math.abs(prev.height - height)
    const prevWasDefault =
      prev.width === DEFAULT_CONTAINER_DIMENSIONS.width && prev.height === DEFAULT_CONTAINER_DIMENSIONS.height

    if (prevWasDefault || widthDiff > MIN_DIMENSION_CHANGE || heightDiff > MIN_DIMENSION_CHANGE) {
      return { width, height }
    }
    return prev
  }

  const clearMeasurementTimeout = useCallback(() => {
    if (measurementTimeoutRef.current) {
      clearTimeout(measurementTimeoutRef.current)
      measurementTimeoutRef.current = null
    }
  }, [])

  const attemptMeasurement = useCallback(() => {
    measurementTimeoutRef.current = null

    const dimensions = calculateTargetDimensions()
    const width = dimensions?.width ?? 0
    const height = dimensions?.height ?? 0
    const hasValidDimensions = width > 0 && height > 0

    measureAttemptsRef.current += 1

    if (!hasValidDimensions && measureAttemptsRef.current < MAX_DIMENSION_MEASURE_ATTEMPTS) {
      measurementTimeoutRef.current = setTimeout(attemptMeasurement, DIMENSION_SETTLE_DELAY_MS)
      return
    }

    if (hasValidDimensions) {
      setContainerDimensions((prev) => getUpdatedContainerDimensions(prev, width, height))
    }
  }, [calculateTargetDimensions])

  useEffect(() => {
    if (!cropImagesFlag) {
      clearMeasurementTimeout()
      return undefined
    }

    measureAttemptsRef.current = 0
    attemptMeasurement()

    return clearMeasurementTimeout
  }, [attemptMeasurement, clearMeasurementTimeout, cropImagesFlag, savedImageUrl])

  const cropInputs = useMemo(() => {
    if (!cropImagesFlag || !savedImageUrl || !savedCropData) {
      return null
    }

    const parsedCropData = parseCropData(savedCropData)
    if (!parsedCropData) {
      return null
    }

    return {
      imageUrl: savedImageUrl,
      parsedCropData,
    }
  }, [cropImagesFlag, savedCropData, savedImageUrl])

  const getCroppedImage = useCallback(async (imageUrl, cropData) => {
    return new Promise((resolve, reject) => {
      try {
        const result = createCroppedImage({
          src: imageUrl,
          x: cropData.x,
          y: cropData.y,
          w: cropData.w,
          h: cropData.h,
          rotation: cropData.rotation || 0,
        })
        resolve(result)
      } catch (error) {
        reject(error instanceof Error ? error : new Error(error))
      }
    })
  }, [])

  useEffect(() => {
    if (!cropInputs) {
      setCroppedImageUrl('')
      setProcessingCrop(false)
      return
    }

    let isCancelled = false
    let cropTimeoutId = null

    const setCropState = (value) => {
      if (!isCancelled) {
        setCroppedImageUrl(value)
        setProcessingCrop(false)
      }
    }

    const tryGetCachedUrl = async () => {
      if (!memoryId) {
        return null
      }

      try {
        return await getCroppedImageCache({
          pageNumber,
          positionIndex,
          memoryId,
          imageUrl: cropInputs.imageUrl,
          cropData: cropInputs.parsedCropData,
        })
      } catch (cacheError) {
        console.error('[useImageCropping] Cropped image cache lookup failed', cacheError)
        return null
      }
    }

    const performCroppingFlow = async () => {
      const cachedUrl = await tryGetCachedUrl()
      if (cachedUrl) {
        setCropState(cachedUrl)
        return
      }

      cropTimeoutId = setTimeout(async () => {
        if (isCancelled) {
          return
        }

        setProcessingCrop(true)

        try {
          const croppedImage = await getCroppedImage(cropInputs.imageUrl, cropInputs.parsedCropData)
          if (isCancelled) {
            return
          }

          if (croppedImage) {
            setCroppedImageUrl(croppedImage)

            if (memoryId) {
              await setCroppedImageCache({
                pageNumber,
                positionIndex,
                memoryId,
                imageUrl: cropInputs.imageUrl,
                cropData: cropInputs.parsedCropData,
                croppedDataUrl: croppedImage,
              })
            }
          } else {
            setCropState('no cropped image')
          }
        } catch (error) {
          console.error('[useImageCropping] Error cropping image', error)
          if (!isCancelled) {
            // Leave croppedImageUrl empty so the component falls back to the original image
            setCroppedImageUrl('')
          }
        } finally {
          if (!isCancelled) {
            setProcessingCrop(false)
          }
        }
      }, 1000)
    }

    performCroppingFlow()

    // eslint-disable-next-line consistent-return
    return () => {
      isCancelled = true
      if (cropTimeoutId) {
        clearTimeout(cropTimeoutId)
      }
    }
  }, [cropInputs, getCroppedImage, memoryId, pageNumber, positionIndex])

  const invalidateCroppedImageCache = useCallback(() => {
    if (!cropImagesFlag) {
      return
    }

    clearCroppedImageCache({ pageNumber, positionIndex }).catch(() => {})
  }, [cropImagesFlag, pageNumber, positionIndex])

  const handleRemove = useCallback(
    (onRemove = () => {}) => {
      invalidateCroppedImageCache()
      setSavedCropData(null)
      setCroppedImageUrl('')
      setProcessingCrop(false)
      onRemove()
    },
    [invalidateCroppedImageCache]
  )

  const handleSave = useCallback(
    async ({ image, cropArea, onSave = async () => {} }) => {
      invalidateCroppedImageCache()

      if (cropImagesFlag) {
        if (cropArea) {
          setSavedCropData(cropArea)
          setProcessingCrop(true)
        } else {
          setSavedCropData(null)
          setProcessingCrop(false)
        }
        setCroppedImageUrl('')
      } else {
        setSavedCropData(null)
        setProcessingCrop(false)
      }

      await onSave(image)
    },
    [cropImagesFlag, invalidateCroppedImageCache]
  )

  const isWaitingForCroppedImage = Boolean(savedCropData && !croppedImageUrl)

  return {
    cropRegionRef,
    containerDimensions,
    croppedImageUrl,
    processingCrop,
    isWaitingForCroppedImage,
    handleRemove,
    handleSave,
  }
}
